import { useSortable, CSS } from '../adapters/dndAdapter';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';
import { getItemClass } from '../utils/dndClassNames';

/**
 * SortableItem - Elemento arrastrable dentro de SortableList
 *
 * Usa el hook useSortable de @dnd-kit internamente
 *
 * @param {string|number} id - ID único del item
 * @param {React.ReactNode|Function} children - Contenido o render function
 * @param {boolean} [disabled=false] - Deshabilitar drag
 * @param {string} [className] - Clase CSS adicional
 * @param {Object} [data] - Datos adicionales para el drag
 * @param {boolean} [useDragHandle=false] - Si true, solo el handle inicia drag
 *
 * Si children es función, recibe:
 * - isDragging: boolean
 * - isOver: boolean
 * - attributes: objeto de atributos de accesibilidad
 * - listeners: event listeners para drag
 * - setNodeRef: ref para el elemento
 * - style: estilos de transformación
 * - dragHandleProps: props para drag handle separado (si useDragHandle=true)
 */
const SortableItem = ({ id, children, disabled = false, className, data, useDragHandle = false }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } = useSortable({
    id: typeof id === 'number' ? String(id) : id,
    disabled,
    data,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Props para el drag handle (si useDragHandle=true, listeners van solo al handle)
  const dragHandleProps = useDragHandle ? { ...listeners } : {};
  const itemListeners = useDragHandle ? {} : listeners;

  // Si children es función, pasarle el estado
  if (typeof children === 'function') {
    return children({
      isDragging,
      isOver,
      attributes,
      listeners: itemListeners,
      setNodeRef,
      style,
      dragHandleProps,
    });
  }

  // Render por defecto
  return (
    <div ref={setNodeRef} style={style} className={clsx(className, getItemClass({ isDragging }))} {...attributes} {...itemListeners}>
      {children}
    </div>
  );
};

SortableItem.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  data: PropTypes.object,
  useDragHandle: PropTypes.bool,
};

export default SortableItem;
